/**
 * Files placed in an outgoing mail — through the editor's "צירוף קובץ" button or a send dialog's
 * attachment field. One upload route serves both (`POST /api/pvt/editor-files`).
 *
 * How a file reaches the recipient depends on the channel (docs/mail-channels.md):
 *  - Smoove cannot attach anything, so the file stays in our bucket and the mail carries a link.
 *  - Gmail attaches it for real, up to `MAIL_ATTACH_MAX_BYTES`; a larger file goes as a link.
 *
 * Dependency-free: the backend imports this file through `@malash-office/types/mail-files`.
 */

/** What the upload accepts: a MIME type prefix (`image/`) or an exact type. */
export const MAIL_FILE_TYPES = [
  'image/',
  'video/',
  'audio/',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
] as const;

/** For the browser's file picker. */
export const MAIL_FILE_ACCEPT = 'image/*,video/*,audio/*,.pdf,.xls,.xlsx,.csv,.doc,.docx';

export const MAIL_FILE_MAX_BYTES = 50 * 1024 * 1024;
/** An image is shown inside the mail itself, so it is held to a size a mail can load. */
export const MAIL_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/** Above this a file in a Gmail send goes as a link: Gmail refuses a message over ~25MB whole. */
export const MAIL_ATTACH_MAX_BYTES = 15 * 1024 * 1024;

const TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav'
};

/**
 * The file's type, from its name when the browser or OS reported none (`''`) or only the generic
 * `application/octet-stream` — which happens to a CSV or an Office file on some systems.
 */
export const resolveMailFileType = (name: string, type: string): string => {
  if (type && type !== 'application/octet-stream') return type;
  return TYPE_BY_EXTENSION[name.split('.').pop()?.toLowerCase() ?? ''] ?? type;
};

export const isMailFileType = (mimeType: string): boolean =>
  MAIL_FILE_TYPES.some(type => (type.endsWith('/') ? mimeType.startsWith(type) : mimeType === type));

/** The Hebrew reason a file is refused before it is uploaded, or null when it may go. */
export const mailFileProblem = (file: { name?: string; type: string; size: number }): string | null => {
  const type = resolveMailFileType(file.name ?? '', file.type);
  if (!isMailFileType(type)) return 'ניתן לצרף תמונה, וידאו, שמע, PDF, אקסל או וורד בלבד';
  if (type.startsWith('image/') && file.size > MAIL_IMAGE_MAX_BYTES) return 'התמונה גדולה מדי. ניתן לצרף תמונה עד 5MB';
  if (file.size > MAIL_FILE_MAX_BYTES) return 'הקובץ גדול מדי. ניתן לצרף קובץ עד 50MB';
  return null;
};

/** A file already in the bucket, as a send body carries it. */
export interface MailFile {
  url: string;
  name: string;
  mimeType: string;
  size: number;
}

export const formatFileSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;

const iconOf = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('video/')) return '🎬';
  if (mimeType.startsWith('audio/')) return '🎵';
  if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType === 'text/csv') return '📊';
  return '📄';
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The link a file becomes inside a mail body. Inline styles only — mail clients drop stylesheets.
 * Used by the editor (Smoove) and by the Gmail sends for a file too large to attach.
 */
export const mailFileLinkHtml = (file: MailFile): string =>
  `<a href="${escapeHtml(file.url)}" target="_blank" rel="noopener noreferrer" ` +
  'style="display:inline-block;padding:6px 12px;border:1px solid #d0d7de;border-radius:8px;text-decoration:none;color:#1a5f6b;">' +
  `${iconOf(file.mimeType)} ${escapeHtml(file.name)} (${formatFileSize(file.size)})</a>`;
